import { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Book,
  Rocket,
  Download,
  Settings,
  Sparkles,
  Terminal,
  GitBranch,
  Puzzle,
  Keyboard,
  Code2,
  Shield,
  Zap,
  ChevronRight,
  Search,
  ExternalLink,
  FileText,
  X,
  Palette,
  Cloud,
  FolderTree,
  MousePointer2,
  Replace,
  GitCommit,
  GitMerge,
  AlertTriangle,
  Store,
  DownloadCloud,
  CheckCircle,
  Command,
  Edit3,
  Monitor,
  Folder,
  Layers,
  RefreshCw
} from 'lucide-react';
import { cn } from '@/lib/cn';

// Search index with keywords for better matching
const docSections = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: Rocket,
    keywords: ['start', 'begin', 'setup', 'install'],
    items: [
      { title: 'Installation', href: '#installation', keywords: ['install', 'download', 'setup', 'macos', 'windows', 'linux'] },
      { title: 'Quick Start', href: '#quick-start', keywords: ['start', 'begin', 'tutorial', 'first', 'project'] },
      { title: 'System Requirements', href: '#requirements', keywords: ['requirements', 'specs', 'minimum', 'ram', 'disk'] },
      { title: 'Migrating from VS Code', href: '#migrate-vscode', keywords: ['migrate', 'vscode', 'import', 'settings', 'extensions'] },
    ]
  },
  {
    id: 'features',
    title: 'Core Features',
    icon: Sparkles,
    keywords: ['features', 'ai', 'capabilities'],
    items: [
      { title: 'AI Agent', href: '#ai-agent', keywords: ['agent', 'ai', 'autonomous', 'refactor', 'generate'] },
      { title: 'Smart Autocomplete', href: '#autocomplete', keywords: ['autocomplete', 'suggestions', 'tab', 'complete', 'intellisense'] },
      { title: 'Codebase Intelligence', href: '#codebase', keywords: ['codebase', 'context', 'understanding', 'index'] },
      { title: 'AI Chat', href: '#ai-chat', keywords: ['chat', 'ask', 'question', 'help', 'explain'] },
    ]
  },
  {
    id: 'editor',
    title: 'Editor',
    icon: Code2,
    keywords: ['editor', 'code', 'write'],
    items: [
      { title: 'Interface Overview', href: '#interface', keywords: ['interface', 'ui', 'layout', 'panels'] },
      { title: 'File Explorer', href: '#file-explorer', keywords: ['files', 'explorer', 'sidebar', 'tree', 'folder'] },
      { title: 'Multi-cursor Editing', href: '#multi-cursor', keywords: ['multi', 'cursor', 'multiple', 'edit', 'select'] },
      { title: 'Search & Replace', href: '#search-replace', keywords: ['search', 'find', 'replace', 'regex'] },
    ]
  },
  {
    id: 'terminal',
    title: 'Terminal',
    icon: Terminal,
    keywords: ['terminal', 'console', 'command', 'shell'],
    items: [
      { title: 'Integrated Terminal', href: '#integrated-terminal', keywords: ['terminal', 'console', 'shell', 'bash', 'zsh'] },
      { title: 'Shell Configuration', href: '#shell-config', keywords: ['shell', 'config', 'bash', 'zsh', 'fish', 'powershell'] },
      { title: 'AI Commands', href: '#ai-commands', keywords: ['ai', 'commands', 'natural', 'language'] },
    ]
  },
  {
    id: 'git',
    title: 'Git Integration',
    icon: GitBranch,
    keywords: ['git', 'version', 'control', 'source'],
    items: [
      { title: 'Source Control', href: '#source-control', keywords: ['git', 'source', 'control', 'version'] },
      { title: 'Commit & Push', href: '#commit-push', keywords: ['commit', 'push', 'save', 'upload'] },
      { title: 'Branch Management', href: '#branches', keywords: ['branch', 'merge', 'checkout', 'switch'] },
      { title: 'Conflict Resolution', href: '#conflicts', keywords: ['conflict', 'merge', 'resolve', 'diff'] },
    ]
  },
  {
    id: 'extensions',
    title: 'Extensions',
    icon: Puzzle,
    keywords: ['extensions', 'plugins', 'addons'],
    items: [
      { title: 'Extension Marketplace', href: '#marketplace', keywords: ['marketplace', 'store', 'browse', 'extensions'] },
      { title: 'Installing Extensions', href: '#install-extensions', keywords: ['install', 'add', 'extension', 'plugin'] },
      { title: 'VS Code Compatibility', href: '#vscode-compat', keywords: ['vscode', 'compatible', 'support', 'extensions'] },
    ]
  },
  {
    id: 'shortcuts',
    title: 'Keyboard Shortcuts',
    icon: Keyboard,
    keywords: ['keyboard', 'shortcuts', 'keys', 'hotkeys'],
    items: [
      { title: 'Default Shortcuts', href: '#default-shortcuts', keywords: ['default', 'shortcuts', 'keys', 'hotkeys'] },
      { title: 'Custom Keybindings', href: '#custom-keybindings', keywords: ['custom', 'keybindings', 'remap', 'change'] },
      { title: 'Vim Mode', href: '#vim-mode', keywords: ['vim', 'modal', 'hjkl', 'neovim'] },
    ]
  },
  {
    id: 'settings',
    title: 'Settings',
    icon: Settings,
    keywords: ['settings', 'preferences', 'config'],
    items: [
      { title: 'User Settings', href: '#user-settings', keywords: ['user', 'settings', 'preferences', 'global'] },
      { title: 'Workspace Settings', href: '#workspace-settings', keywords: ['workspace', 'project', 'settings', 'local'] },
      { title: 'Themes', href: '#themes', keywords: ['theme', 'dark', 'light', 'color', 'appearance'] },
      { title: 'Sync Settings', href: '#sync', keywords: ['sync', 'cloud', 'backup', 'restore'] },
    ]
  },
];

const quickLinks = [
  { title: 'Download Ochtopus', href: '/pricing', icon: Download },
  { title: 'Keyboard Shortcuts PDF', href: '/docs#default-shortcuts', icon: Keyboard },
  { title: 'GitHub Repository', href: 'https://github.com/ochtopus/ochtopus', icon: ExternalLink },
  { title: 'Community Discord', href: 'https://discord.gg/ochtopus', icon: ExternalLink },
];

interface SearchResult {
  type: 'section' | 'item';
  title: string;
  href: string;
  sectionTitle?: string;
  icon: typeof Rocket;
  relevance: number;
}

export function DocsPage() {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Smart search function
  const searchResults = useMemo<SearchResult[]>(() => {
    if (searchQuery.length < 2) return [];

    const query = searchQuery.toLowerCase().trim();
    const results: SearchResult[] = [];

    docSections.forEach((section) => {
      const sectionRelevance = calculateRelevance(query, section.title, section.keywords);

      if (sectionRelevance > 0) {
        results.push({
          type: 'section',
          title: section.title,
          href: `#${section.id}`,
          icon: section.icon,
          relevance: sectionRelevance,
        });
      }

      section.items.forEach((item) => {
        const itemRelevance = calculateRelevance(query, item.title, item.keywords);

        if (itemRelevance > 0) {
          results.push({
            type: 'item',
            title: item.title,
            href: item.href,
            sectionTitle: section.title,
            icon: section.icon,
            relevance: itemRelevance,
          });
        }
      });
    });

    return results.sort((a, b) => b.relevance - a.relevance).slice(0, 8);
  }, [searchQuery]);

  function calculateRelevance(query: string, title: string, keywords: string[]): number {
    const titleLower = title.toLowerCase();
    let score = 0;

    if (titleLower === query) score += 100;
    else if (titleLower.startsWith(query)) score += 80;
    else if (titleLower.includes(query)) score += 60;

    keywords.forEach((keyword) => {
      if (keyword === query) score += 50;
      else if (keyword.startsWith(query)) score += 30;
      else if (keyword.includes(query)) score += 20;
    });

    if (score === 0) {
      let queryIndex = 0;
      for (let i = 0; i < titleLower.length && queryIndex < query.length; i++) {
        if (titleLower[i] === query[queryIndex]) queryIndex++;
      }
      if (queryIndex === query.length) score += 10;
    }

    return score;
  }

  function highlightMatch(text: string, query: string): React.ReactNode {
    if (query.length < 2) return text;
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerText.indexOf(lowerQuery);
    if (index === -1) return text;

    return (
      <>
        {text.slice(0, index)}
        <span className="bg-accent/30 text-accent font-medium">
          {text.slice(index, index + query.length)}
        </span>
        {text.slice(index + query.length)}
      </>
    );
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isSearchOpen || searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % searchResults.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + searchResults.length) % searchResults.length);
        break;
      case 'Enter':
        e.preventDefault();
        handleSelectResult(searchResults[selectedIndex]);
        break;
      case 'Escape':
        setIsSearchOpen(false);
        setSearchQuery('');
        break;
    }
  };

  const handleSelectResult = (result: SearchResult) => {
    setSearchQuery('');
    setIsSearchOpen(false);

    if (result.type === 'section') {
      setActiveSection(result.href.replace('#', ''));
    } else {
      const parentSection = docSections.find((s) => s.items.some((i) => i.href === result.href));
      if (parentSection) setActiveSection(parentSection.id);
    }

    setTimeout(() => {
      const element = document.querySelector(result.href);
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectedIndex(0);
  }, [searchResults]);

  useEffect(() => {
    if (searchQuery.length >= 2) setIsSearchOpen(true);
    else setIsSearchOpen(false);
  }, [searchQuery]);

  return (
    <div className="pt-24 min-h-screen">
      <div className="container py-8">
        <div className="grid lg:grid-cols-[280px_1fr_200px] gap-8">
          {/* Sidebar Navigation */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-6">
              {/* Search */}
              <div className="relative" ref={searchRef}>
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary z-10" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder={t('docs.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={() => searchQuery.length >= 2 && setIsSearchOpen(true)}
                  className="w-full pl-10 pr-8 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none text-sm"
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setIsSearchOpen(false);
                      inputRef.current?.focus();
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
                  >
                    <X size={14} />
                  </button>
                )}

                {/* Search Results Dropdown */}
                {isSearchOpen && searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-bg-card border border-border-default rounded-lg shadow-xl overflow-hidden z-50">
                    <div className="p-2 border-b border-border-default">
                      <span className="text-xs text-text-tertiary">
                        {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
                      </span>
                    </div>
                    <ul className="max-h-80 overflow-y-auto">
                      {searchResults.map((result, index) => (
                        <li key={`${result.type}-${result.href}`}>
                          <button
                            onClick={() => handleSelectResult(result)}
                            onMouseEnter={() => setSelectedIndex(index)}
                            className={cn(
                              'w-full flex items-start gap-3 px-3 py-2.5 text-left transition-colors',
                              selectedIndex === index ? 'bg-accent/10 text-accent' : 'hover:bg-bg-elevated text-text-primary'
                            )}
                          >
                            <result.icon size={16} className={cn('mt-0.5 flex-shrink-0', selectedIndex === index ? 'text-accent' : 'text-text-tertiary')} />
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-sm truncate">{highlightMatch(result.title, searchQuery)}</div>
                              {result.sectionTitle && <div className="text-xs text-text-tertiary truncate">in {result.sectionTitle}</div>}
                            </div>
                            {result.type === 'section' && <span className="text-xs px-1.5 py-0.5 rounded bg-bg-elevated text-text-tertiary">Section</span>}
                          </button>
                        </li>
                      ))}
                    </ul>
                    <div className="p-2 border-t border-border-default text-xs text-text-tertiary flex items-center justify-between">
                      <span><kbd className="px-1.5 py-0.5 rounded bg-bg-elevated">↑↓</kbd> navigate</span>
                      <span><kbd className="px-1.5 py-0.5 rounded bg-bg-elevated">↵</kbd> select</span>
                      <span><kbd className="px-1.5 py-0.5 rounded bg-bg-elevated">esc</kbd> close</span>
                    </div>
                  </div>
                )}

                {isSearchOpen && searchQuery.length >= 2 && searchResults.length === 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-bg-card border border-border-default rounded-lg shadow-xl p-4 text-center z-50">
                    <FileText size={24} className="mx-auto mb-2 text-text-tertiary" />
                    <p className="text-sm text-text-secondary">No results for "{searchQuery}"</p>
                    <p className="text-xs text-text-tertiary mt-1">Try different keywords</p>
                  </div>
                )}
              </div>

              {/* Navigation */}
              <nav className="space-y-4">
                {docSections.map((section) => (
                  <div key={section.id}>
                    <button
                      onClick={() => setActiveSection(section.id)}
                      className={cn(
                        'flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg transition-colors',
                        activeSection === section.id ? 'bg-accent-muted text-accent' : 'text-text-secondary hover:text-text-primary hover:bg-bg-card'
                      )}
                    >
                      <section.icon size={18} />
                      <span className="font-medium">{section.title}</span>
                    </button>
                    {activeSection === section.id && (
                      <ul className="mt-2 ml-6 space-y-1">
                        {section.items.map((item) => (
                          <li key={item.title}>
                            <a href={item.href} className="block py-1.5 text-sm text-text-tertiary hover:text-accent transition-colors">
                              {item.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="min-w-0">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-2 text-text-tertiary text-sm mb-4">
                <Link to="/" className="hover:text-accent">{t('docs.breadcrumb.home')}</Link>
                <ChevronRight size={14} />
                <span>{t('docs.breadcrumb.docs')}</span>
              </div>
              <h1 className="heading-xl mb-4">{t('docs.title')}</h1>
              <p className="text-text-secondary text-lg">
                {t('docs.subtitle')}
              </p>
            </div>

            {/* ==================== GETTING STARTED ==================== */}
            <section id="installation" className="mb-12">
              <h2 className="heading-lg mb-6 flex items-center gap-3">
                <Rocket className="text-accent" />
                Getting Started
              </h2>

              <div className="card mb-6">
                <h3 className="heading-sm mb-4">Installation</h3>
                <p className="text-text-secondary mb-4">
                  Download Ochtopus for your operating system and follow the installation instructions.
                </p>

                <div className="grid sm:grid-cols-3 gap-4 mb-6">
                  <a href="#" className="p-4 rounded-lg bg-bg-elevated border border-border-default hover:border-accent transition-colors text-center">
                    <Download className="mx-auto mb-2 text-accent" size={24} />
                    <div className="font-medium">macOS</div>
                    <div className="text-text-tertiary text-sm">Intel & Apple Silicon</div>
                  </a>
                  <a href="#" className="p-4 rounded-lg bg-bg-elevated border border-border-default hover:border-accent transition-colors text-center">
                    <Download className="mx-auto mb-2 text-accent" size={24} />
                    <div className="font-medium">Windows</div>
                    <div className="text-text-tertiary text-sm">Windows 10+</div>
                  </a>
                  <a href="#" className="p-4 rounded-lg bg-bg-elevated border border-border-default hover:border-accent transition-colors text-center">
                    <Download className="mx-auto mb-2 text-accent" size={24} />
                    <div className="font-medium">Linux</div>
                    <div className="text-text-tertiary text-sm">.deb, .rpm, AppImage</div>
                  </a>
                </div>

                <h4 className="font-semibold mb-2" id="requirements">System Requirements</h4>
                <ul className="list-disc list-inside text-text-secondary space-y-1">
                  <li>macOS 10.15+ / Windows 10+ / Ubuntu 18.04+</li>
                  <li>4GB RAM minimum (8GB recommended)</li>
                  <li>500MB disk space</li>
                  <li>Internet connection for AI features</li>
                </ul>
              </div>

              <div className="card mb-6" id="quick-start">
                <h3 className="heading-sm mb-4">Quick Start</h3>
                <p className="text-text-secondary mb-4">Get up and running with Ochtopus in minutes:</p>

                <ol className="space-y-4">
                  <li className="flex gap-4">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center font-bold">1</span>
                    <div>
                      <h4 className="font-semibold mb-1">Download and Install</h4>
                      <p className="text-text-secondary text-sm">Download the installer for your OS and run it.</p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center font-bold">2</span>
                    <div>
                      <h4 className="font-semibold mb-1">Open a Project</h4>
                      <p className="text-text-secondary text-sm">Use File → Open Folder to open your project directory.</p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center font-bold">3</span>
                    <div>
                      <h4 className="font-semibold mb-1">Start Coding with AI</h4>
                      <p className="text-text-secondary text-sm">Press <code className="px-2 py-0.5 bg-bg-elevated rounded">Cmd/Ctrl + K</code> to open AI chat.</p>
                    </div>
                  </li>
                </ol>
              </div>

              <div className="card" id="migrate-vscode">
                <h3 className="heading-sm mb-4">Migrating from VS Code</h3>
                <p className="text-text-secondary mb-4">
                  Ochtopus makes it easy to migrate from VS Code. Your settings, extensions, and keybindings can be imported with one click.
                </p>

                <div className="p-4 rounded-lg bg-accent-glow border border-accent/20 mb-4">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Zap className="text-accent" size={18} />
                    One-Click Import
                  </h4>
                  <p className="text-text-secondary text-sm">
                    Go to Settings → Import from VS Code to automatically import your configuration.
                  </p>
                </div>

                <p className="text-text-secondary text-sm">The following will be imported:</p>
                <ul className="list-disc list-inside text-text-secondary text-sm mt-2 space-y-1">
                  <li>User settings and preferences</li>
                  <li>Installed extensions (VS Code compatible)</li>
                  <li>Keyboard shortcuts</li>
                  <li>Snippets</li>
                  <li>Themes</li>
                </ul>
              </div>
            </section>

            {/* ==================== CORE FEATURES ==================== */}
            <section id="ai-agent" className="mb-12">
              <h2 className="heading-lg mb-6 flex items-center gap-3">
                <Sparkles className="text-accent" />
                Core Features
              </h2>

              <div className="card mb-6">
                <h3 className="heading-sm mb-4">AI Agent</h3>
                <p className="text-text-secondary mb-4">
                  The AI Agent is your intelligent coding partner that can handle complex tasks autonomously.
                </p>

                <h4 className="font-semibold mb-2">Capabilities</h4>
                <ul className="list-disc list-inside text-text-secondary space-y-1 mb-4">
                  <li>Multi-file refactoring</li>
                  <li>Automatic test generation</li>
                  <li>Bug detection and fixing</li>
                  <li>Code documentation</li>
                  <li>Natural language to code</li>
                </ul>

                <h4 className="font-semibold mb-2">How to Use</h4>
                <p className="text-text-secondary mb-2">
                  Open the AI panel with <code className="px-2 py-0.5 bg-bg-elevated rounded">Cmd/Ctrl + Shift + A</code> and describe what you want to accomplish.
                </p>

                <div className="p-4 rounded-lg bg-bg-elevated font-mono text-sm">
                  <span className="text-text-tertiary"># Example prompt:</span><br />
                  <span className="text-accent">"Add authentication to the user routes with JWT tokens and refresh token support"</span>
                </div>
              </div>

              <div className="card mb-6" id="autocomplete">
                <h3 className="heading-sm mb-4">Smart Autocomplete</h3>
                <p className="text-text-secondary mb-4">Our custom-trained model provides magically accurate code completions.</p>

                <h4 className="font-semibold mb-2">Features</h4>
                <ul className="list-disc list-inside text-text-secondary space-y-1 mb-4">
                  <li>Multi-line completions</li>
                  <li>Context-aware suggestions</li>
                  <li>Import auto-completion</li>
                  <li>Type inference</li>
                </ul>

                <h4 className="font-semibold mb-2">Keyboard Shortcuts</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 bg-bg-elevated rounded"><code>Tab</code> - Accept suggestion</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>Esc</code> - Dismiss suggestion</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>Cmd/Ctrl + →</code> - Accept word</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>Alt + ]</code> - Next suggestion</div>
                </div>
              </div>

              <div className="card mb-6" id="codebase">
                <h3 className="heading-sm mb-4">Codebase Intelligence</h3>
                <p className="text-text-secondary mb-4">
                  Ochtopus indexes your entire codebase to provide context-aware assistance.
                </p>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div className="p-4 rounded-lg bg-bg-elevated">
                    <h4 className="font-semibold mb-2">Symbol Navigation</h4>
                    <p className="text-text-secondary text-sm">Jump to definitions, find references, and explore your code structure.</p>
                  </div>
                  <div className="p-4 rounded-lg bg-bg-elevated">
                    <h4 className="font-semibold mb-2">Semantic Search</h4>
                    <p className="text-text-secondary text-sm">Search your codebase using natural language queries.</p>
                  </div>
                  <div className="p-4 rounded-lg bg-bg-elevated">
                    <h4 className="font-semibold mb-2">Dependency Graph</h4>
                    <p className="text-text-secondary text-sm">Visualize relationships between files and modules.</p>
                  </div>
                  <div className="p-4 rounded-lg bg-bg-elevated">
                    <h4 className="font-semibold mb-2">Auto-imports</h4>
                    <p className="text-text-secondary text-sm">Automatically import symbols from anywhere in your project.</p>
                  </div>
                </div>
              </div>

              <div className="card" id="ai-chat">
                <h3 className="heading-sm mb-4">AI Chat</h3>
                <p className="text-text-secondary mb-4">Chat with AI about your code. Get explanations, suggestions, and help.</p>

                <h4 className="font-semibold mb-2">Open AI Chat</h4>
                <p className="text-text-secondary mb-4">
                  Press <code className="px-2 py-0.5 bg-bg-elevated rounded">Cmd/Ctrl + K</code> to open inline chat, or use the AI panel for longer conversations.
                </p>

                <h4 className="font-semibold mb-2">Special Commands</h4>
                <div className="space-y-2 text-sm">
                  <div className="p-2 bg-bg-elevated rounded"><code>@file</code> - Reference specific files in your prompt</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>@docs</code> - Include documentation context</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>@web</code> - Search the web for information</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>@codebase</code> - Search your entire codebase</div>
                </div>
              </div>
            </section>

            {/* ==================== EDITOR ==================== */}
            <section id="interface" className="mb-12">
              <h2 className="heading-lg mb-6 flex items-center gap-3">
                <Code2 className="text-accent" />
                Editor
              </h2>

              <div className="card mb-6">
                <h3 className="heading-sm mb-4">Interface Overview</h3>
                <p className="text-text-secondary mb-4">
                  Ochtopus features a modern, customizable interface designed for productivity.
                </p>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div className="p-4 rounded-lg bg-bg-elevated">
                    <Monitor className="text-accent mb-2" size={20} />
                    <h4 className="font-semibold mb-1">Activity Bar</h4>
                    <p className="text-text-secondary text-sm">Quick access to Explorer, Search, Source Control, Extensions, and AI.</p>
                  </div>
                  <div className="p-4 rounded-lg bg-bg-elevated">
                    <Layers className="text-accent mb-2" size={20} />
                    <h4 className="font-semibold mb-1">Editor Groups</h4>
                    <p className="text-text-secondary text-sm">Split your editor into multiple groups for side-by-side editing.</p>
                  </div>
                  <div className="p-4 rounded-lg bg-bg-elevated">
                    <Terminal className="text-accent mb-2" size={20} />
                    <h4 className="font-semibold mb-1">Panel</h4>
                    <p className="text-text-secondary text-sm">Terminal, Problems, Output, and Debug Console at the bottom.</p>
                  </div>
                  <div className="p-4 rounded-lg bg-bg-elevated">
                    <Sparkles className="text-accent mb-2" size={20} />
                    <h4 className="font-semibold mb-1">AI Sidebar</h4>
                    <p className="text-text-secondary text-sm">Dedicated space for AI chat and agent interactions.</p>
                  </div>
                </div>
              </div>

              <div className="card mb-6" id="file-explorer">
                <h3 className="heading-sm mb-4 flex items-center gap-2">
                  <FolderTree size={20} />
                  File Explorer
                </h3>
                <p className="text-text-secondary mb-4">
                  Navigate your project files with the integrated file explorer.
                </p>

                <h4 className="font-semibold mb-2">Features</h4>
                <ul className="list-disc list-inside text-text-secondary space-y-1 mb-4">
                  <li>Drag and drop file management</li>
                  <li>Quick file creation with templates</li>
                  <li>File icons based on type and content</li>
                  <li>Breadcrumb navigation</li>
                  <li>File filtering and search</li>
                </ul>

                <h4 className="font-semibold mb-2">Shortcuts</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 bg-bg-elevated rounded"><code>Cmd/Ctrl + P</code> - Quick Open</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>Cmd/Ctrl + Shift + E</code> - Focus Explorer</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>Cmd/Ctrl + N</code> - New File</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>F2</code> - Rename File</div>
                </div>
              </div>

              <div className="card mb-6" id="multi-cursor">
                <h3 className="heading-sm mb-4 flex items-center gap-2">
                  <MousePointer2 size={20} />
                  Multi-cursor Editing
                </h3>
                <p className="text-text-secondary mb-4">
                  Edit multiple locations simultaneously with multi-cursor support.
                </p>

                <h4 className="font-semibold mb-2">Add Cursors</h4>
                <div className="grid grid-cols-1 gap-2 text-sm mb-4">
                  <div className="p-2 bg-bg-elevated rounded"><code>Alt + Click</code> - Add cursor at click position</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>Cmd/Ctrl + Alt + ↑/↓</code> - Add cursor above/below</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>Cmd/Ctrl + D</code> - Select next occurrence</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>Cmd/Ctrl + Shift + L</code> - Select all occurrences</div>
                </div>

                <div className="p-4 rounded-lg bg-accent-glow border border-accent/20">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Zap className="text-accent" size={18} />
                    Pro Tip
                  </h4>
                  <p className="text-text-secondary text-sm">
                    Use <code className="px-1 py-0.5 bg-bg-elevated rounded">Cmd/Ctrl + Shift + L</code> to quickly rename all occurrences of a variable.
                  </p>
                </div>
              </div>

              <div className="card" id="search-replace">
                <h3 className="heading-sm mb-4 flex items-center gap-2">
                  <Replace size={20} />
                  Search & Replace
                </h3>
                <p className="text-text-secondary mb-4">
                  Powerful search and replace across files with regex support.
                </p>

                <h4 className="font-semibold mb-2">Search Options</h4>
                <ul className="list-disc list-inside text-text-secondary space-y-1 mb-4">
                  <li>Case sensitive matching</li>
                  <li>Whole word matching</li>
                  <li>Regular expression support</li>
                  <li>Search in selection</li>
                  <li>Include/exclude file patterns</li>
                </ul>

                <h4 className="font-semibold mb-2">Shortcuts</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 bg-bg-elevated rounded"><code>Cmd/Ctrl + F</code> - Find in file</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>Cmd/Ctrl + H</code> - Replace in file</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>Cmd/Ctrl + Shift + F</code> - Find in files</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>Cmd/Ctrl + Shift + H</code> - Replace in files</div>
                </div>
              </div>
            </section>

            {/* ==================== TERMINAL ==================== */}
            <section id="integrated-terminal" className="mb-12">
              <h2 className="heading-lg mb-6 flex items-center gap-3">
                <Terminal className="text-accent" />
                Terminal
              </h2>

              <div className="card mb-6">
                <h3 className="heading-sm mb-4">Integrated Terminal</h3>
                <p className="text-text-secondary mb-4">
                  A fully-featured terminal integrated directly into the editor.
                </p>

                <h4 className="font-semibold mb-2">Features</h4>
                <ul className="list-disc list-inside text-text-secondary space-y-1 mb-4">
                  <li>Multiple terminal instances</li>
                  <li>Split terminals horizontally or vertically</li>
                  <li>Full Unicode and emoji support</li>
                  <li>Customizable shell profiles</li>
                  <li>Auto-detection of running tasks</li>
                </ul>

                <h4 className="font-semibold mb-2">Shortcuts</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 bg-bg-elevated rounded"><code>Ctrl + `</code> - Toggle terminal</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>Cmd/Ctrl + Shift + `</code> - New terminal</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>Cmd/Ctrl + \</code> - Split terminal</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>Cmd/Ctrl + W</code> - Close terminal</div>
                </div>
              </div>

              <div className="card mb-6" id="shell-config">
                <h3 className="heading-sm mb-4">Shell Configuration</h3>
                <p className="text-text-secondary mb-4">
                  Configure your preferred shell and customize terminal behavior.
                </p>

                <h4 className="font-semibold mb-2">Supported Shells</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm mb-4">
                  <div className="p-3 bg-bg-elevated rounded text-center">Bash</div>
                  <div className="p-3 bg-bg-elevated rounded text-center">Zsh</div>
                  <div className="p-3 bg-bg-elevated rounded text-center">Fish</div>
                  <div className="p-3 bg-bg-elevated rounded text-center">PowerShell</div>
                </div>

                <h4 className="font-semibold mb-2">Configuration</h4>
                <div className="p-4 rounded-lg bg-bg-elevated font-mono text-sm">
                  <pre className="text-text-secondary">{`// settings.json
{
  "terminal.defaultProfile.osx": "zsh",
  "terminal.fontSize": 14,
  "terminal.fontFamily": "JetBrains Mono"
}`}</pre>
                </div>
              </div>

              <div className="card" id="ai-commands">
                <h3 className="heading-sm mb-4">AI Commands</h3>
                <p className="text-text-secondary mb-4">
                  Use natural language to execute terminal commands.
                </p>

                <div className="p-4 rounded-lg bg-accent-glow border border-accent/20 mb-4">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Sparkles className="text-accent" size={18} />
                    Natural Language Commands
                  </h4>
                  <p className="text-text-secondary text-sm">
                    Press <code className="px-1 py-0.5 bg-bg-elevated rounded">Cmd/Ctrl + I</code> in terminal and type what you want to do.
                  </p>
                </div>

                <h4 className="font-semibold mb-2">Examples</h4>
                <div className="space-y-2 text-sm">
                  <div className="p-3 bg-bg-elevated rounded">
                    <span className="text-text-tertiary">"find all js files modified today"</span>
                    <span className="text-accent ml-2">→ find . -name "*.js" -mtime 0</span>
                  </div>
                  <div className="p-3 bg-bg-elevated rounded">
                    <span className="text-text-tertiary">"kill process on port 3000"</span>
                    <span className="text-accent ml-2">→ lsof -ti:3000 | xargs kill</span>
                  </div>
                  <div className="p-3 bg-bg-elevated rounded">
                    <span className="text-text-tertiary">"compress all images in current folder"</span>
                    <span className="text-accent ml-2">→ for f in *.{'{'}jpg,png{'}'}; do convert "$f" -quality 80 "$f"; done</span>
                  </div>
                </div>
              </div>
            </section>

            {/* ==================== GIT INTEGRATION ==================== */}
            <section id="source-control" className="mb-12">
              <h2 className="heading-lg mb-6 flex items-center gap-3">
                <GitBranch className="text-accent" />
                Git Integration
              </h2>

              <div className="card mb-6">
                <h3 className="heading-sm mb-4">Source Control</h3>
                <p className="text-text-secondary mb-4">
                  Full Git integration with visual diff, staging, and commit management.
                </p>

                <h4 className="font-semibold mb-2">Features</h4>
                <ul className="list-disc list-inside text-text-secondary space-y-1 mb-4">
                  <li>Visual diff viewer with inline changes</li>
                  <li>Stage/unstage individual lines or hunks</li>
                  <li>Commit history with graph view</li>
                  <li>Blame annotations</li>
                  <li>Stash management</li>
                </ul>

                <h4 className="font-semibold mb-2">Shortcuts</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 bg-bg-elevated rounded"><code>Cmd/Ctrl + Shift + G</code> - Open Source Control</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>Cmd/Ctrl + Enter</code> - Commit staged</div>
                </div>
              </div>

              <div className="card mb-6" id="commit-push">
                <h3 className="heading-sm mb-4 flex items-center gap-2">
                  <GitCommit size={20} />
                  Commit & Push
                </h3>
                <p className="text-text-secondary mb-4">
                  Stage, commit, and push your changes with ease.
                </p>

                <ol className="space-y-3 mb-4">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent text-white flex items-center justify-center text-sm font-bold">1</span>
                    <div>
                      <h4 className="font-semibold text-sm">Stage Changes</h4>
                      <p className="text-text-secondary text-sm">Click + next to files or use <code className="px-1 py-0.5 bg-bg-elevated rounded text-xs">Stage All</code></p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent text-white flex items-center justify-center text-sm font-bold">2</span>
                    <div>
                      <h4 className="font-semibold text-sm">Write Message</h4>
                      <p className="text-text-secondary text-sm">Enter a descriptive commit message in the input box</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent text-white flex items-center justify-center text-sm font-bold">3</span>
                    <div>
                      <h4 className="font-semibold text-sm">Commit & Push</h4>
                      <p className="text-text-secondary text-sm">Press <code className="px-1 py-0.5 bg-bg-elevated rounded text-xs">Cmd/Ctrl + Enter</code> to commit</p>
                    </div>
                  </li>
                </ol>

                <div className="p-4 rounded-lg bg-accent-glow border border-accent/20">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Sparkles className="text-accent" size={18} />
                    AI Commit Messages
                  </h4>
                  <p className="text-text-secondary text-sm">
                    Click the sparkle icon to generate a commit message based on your changes.
                  </p>
                </div>
              </div>

              <div className="card mb-6" id="branches">
                <h3 className="heading-sm mb-4 flex items-center gap-2">
                  <GitBranch size={20} />
                  Branch Management
                </h3>
                <p className="text-text-secondary mb-4">
                  Create, switch, and merge branches with visual tools.
                </p>

                <h4 className="font-semibold mb-2">Common Operations</h4>
                <div className="grid grid-cols-1 gap-2 text-sm mb-4">
                  <div className="p-3 bg-bg-elevated rounded flex items-center justify-between">
                    <span>Create Branch</span>
                    <code className="text-accent">Click branch name → Create Branch</code>
                  </div>
                  <div className="p-3 bg-bg-elevated rounded flex items-center justify-between">
                    <span>Switch Branch</span>
                    <code className="text-accent">Click branch name → Select branch</code>
                  </div>
                  <div className="p-3 bg-bg-elevated rounded flex items-center justify-between">
                    <span>Merge Branch</span>
                    <code className="text-accent">Right-click branch → Merge into current</code>
                  </div>
                  <div className="p-3 bg-bg-elevated rounded flex items-center justify-between">
                    <span>Delete Branch</span>
                    <code className="text-accent">Right-click branch → Delete</code>
                  </div>
                </div>
              </div>

              <div className="card" id="conflicts">
                <h3 className="heading-sm mb-4 flex items-center gap-2">
                  <AlertTriangle size={20} />
                  Conflict Resolution
                </h3>
                <p className="text-text-secondary mb-4">
                  Resolve merge conflicts with a visual 3-way merge editor.
                </p>

                <div className="grid sm:grid-cols-3 gap-4 mb-4">
                  <div className="p-4 rounded-lg bg-bg-elevated text-center">
                    <div className="font-semibold mb-1">Current</div>
                    <p className="text-text-secondary text-sm">Your local changes</p>
                  </div>
                  <div className="p-4 rounded-lg bg-bg-elevated text-center">
                    <div className="font-semibold mb-1">Result</div>
                    <p className="text-text-secondary text-sm">Final merged output</p>
                  </div>
                  <div className="p-4 rounded-lg bg-bg-elevated text-center">
                    <div className="font-semibold mb-1">Incoming</div>
                    <p className="text-text-secondary text-sm">Changes to merge</p>
                  </div>
                </div>

                <h4 className="font-semibold mb-2">Quick Actions</h4>
                <ul className="list-disc list-inside text-text-secondary space-y-1">
                  <li><strong>Accept Current</strong> - Keep your changes</li>
                  <li><strong>Accept Incoming</strong> - Use incoming changes</li>
                  <li><strong>Accept Both</strong> - Include both versions</li>
                  <li><strong>Compare</strong> - View side-by-side diff</li>
                </ul>
              </div>
            </section>

            {/* ==================== EXTENSIONS ==================== */}
            <section id="marketplace" className="mb-12">
              <h2 className="heading-lg mb-6 flex items-center gap-3">
                <Puzzle className="text-accent" />
                Extensions
              </h2>

              <div className="card mb-6">
                <h3 className="heading-sm mb-4 flex items-center gap-2">
                  <Store size={20} />
                  Extension Marketplace
                </h3>
                <p className="text-text-secondary mb-4">
                  Browse and install extensions to customize your editor.
                </p>

                <h4 className="font-semibold mb-2">Popular Categories</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm mb-4">
                  <div className="p-3 bg-bg-elevated rounded text-center">Languages</div>
                  <div className="p-3 bg-bg-elevated rounded text-center">Themes</div>
                  <div className="p-3 bg-bg-elevated rounded text-center">Formatters</div>
                  <div className="p-3 bg-bg-elevated rounded text-center">AI Tools</div>
                </div>

                <h4 className="font-semibold mb-2">Featured Extensions</h4>
                <div className="space-y-2">
                  <div className="p-3 bg-bg-elevated rounded flex items-center justify-between">
                    <div>
                      <div className="font-medium">Prettier</div>
                      <div className="text-text-tertiary text-sm">Code formatter</div>
                    </div>
                    <span className="text-success text-sm">10M+ installs</span>
                  </div>
                  <div className="p-3 bg-bg-elevated rounded flex items-center justify-between">
                    <div>
                      <div className="font-medium">ESLint</div>
                      <div className="text-text-tertiary text-sm">JavaScript linter</div>
                    </div>
                    <span className="text-success text-sm">8M+ installs</span>
                  </div>
                  <div className="p-3 bg-bg-elevated rounded flex items-center justify-between">
                    <div>
                      <div className="font-medium">GitLens</div>
                      <div className="text-text-tertiary text-sm">Git supercharged</div>
                    </div>
                    <span className="text-success text-sm">5M+ installs</span>
                  </div>
                </div>
              </div>

              <div className="card mb-6" id="install-extensions">
                <h3 className="heading-sm mb-4 flex items-center gap-2">
                  <DownloadCloud size={20} />
                  Installing Extensions
                </h3>
                <p className="text-text-secondary mb-4">
                  Install extensions from the marketplace or from VSIX files.
                </p>

                <h4 className="font-semibold mb-2">From Marketplace</h4>
                <ol className="list-decimal list-inside text-text-secondary space-y-2 mb-4">
                  <li>Open Extensions view (<code className="px-1 py-0.5 bg-bg-elevated rounded text-xs">Cmd/Ctrl + Shift + X</code>)</li>
                  <li>Search for the extension</li>
                  <li>Click Install</li>
                  <li>Reload if prompted</li>
                </ol>

                <h4 className="font-semibold mb-2">From VSIX</h4>
                <p className="text-text-secondary text-sm">
                  Open Command Palette → "Extensions: Install from VSIX..." → Select file
                </p>
              </div>

              <div className="card" id="vscode-compat">
                <h3 className="heading-sm mb-4 flex items-center gap-2">
                  <CheckCircle size={20} />
                  VS Code Compatibility
                </h3>
                <p className="text-text-secondary mb-4">
                  Ochtopus is compatible with most VS Code extensions.
                </p>

                <div className="p-4 rounded-lg bg-success/10 border border-success/20 mb-4">
                  <h4 className="font-semibold mb-2 text-success">Fully Compatible</h4>
                  <ul className="list-disc list-inside text-text-secondary text-sm space-y-1">
                    <li>Language extensions (syntax highlighting, snippets)</li>
                    <li>Themes and icon packs</li>
                    <li>Linters and formatters</li>
                    <li>Debug adapters</li>
                  </ul>
                </div>

                <div className="p-4 rounded-lg bg-warning/10 border border-warning/20">
                  <h4 className="font-semibold mb-2 text-warning">Limited Support</h4>
                  <ul className="list-disc list-inside text-text-secondary text-sm space-y-1">
                    <li>Extensions using proprietary VS Code APIs</li>
                    <li>Remote development extensions</li>
                    <li>Some notebook extensions</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* ==================== KEYBOARD SHORTCUTS ==================== */}
            <section id="default-shortcuts" className="mb-12">
              <h2 className="heading-lg mb-6 flex items-center gap-3">
                <Keyboard className="text-accent" />
                Keyboard Shortcuts
              </h2>

              <div className="card mb-6">
                <h3 className="heading-sm mb-4">Default Shortcuts</h3>
                <p className="text-text-secondary mb-4">
                  Essential keyboard shortcuts for efficient editing.
                </p>

                <h4 className="font-semibold mb-2">General</h4>
                <div className="grid grid-cols-1 gap-1 text-sm mb-4">
                  <div className="p-2 bg-bg-elevated rounded flex justify-between"><span>Command Palette</span><code>Cmd/Ctrl + Shift + P</code></div>
                  <div className="p-2 bg-bg-elevated rounded flex justify-between"><span>Quick Open</span><code>Cmd/Ctrl + P</code></div>
                  <div className="p-2 bg-bg-elevated rounded flex justify-between"><span>New Window</span><code>Cmd/Ctrl + Shift + N</code></div>
                  <div className="p-2 bg-bg-elevated rounded flex justify-between"><span>Close Window</span><code>Cmd/Ctrl + W</code></div>
                  <div className="p-2 bg-bg-elevated rounded flex justify-between"><span>Settings</span><code>Cmd/Ctrl + ,</code></div>
                </div>

                <h4 className="font-semibold mb-2">Editing</h4>
                <div className="grid grid-cols-1 gap-1 text-sm mb-4">
                  <div className="p-2 bg-bg-elevated rounded flex justify-between"><span>Cut Line</span><code>Cmd/Ctrl + X</code></div>
                  <div className="p-2 bg-bg-elevated rounded flex justify-between"><span>Copy Line</span><code>Cmd/Ctrl + C</code></div>
                  <div className="p-2 bg-bg-elevated rounded flex justify-between"><span>Move Line Up/Down</span><code>Alt + ↑/↓</code></div>
                  <div className="p-2 bg-bg-elevated rounded flex justify-between"><span>Duplicate Line</span><code>Shift + Alt + ↑/↓</code></div>
                  <div className="p-2 bg-bg-elevated rounded flex justify-between"><span>Delete Line</span><code>Cmd/Ctrl + Shift + K</code></div>
                  <div className="p-2 bg-bg-elevated rounded flex justify-between"><span>Toggle Comment</span><code>Cmd/Ctrl + /</code></div>
                </div>

                <h4 className="font-semibold mb-2">AI Features</h4>
                <div className="grid grid-cols-1 gap-1 text-sm">
                  <div className="p-2 bg-bg-elevated rounded flex justify-between"><span>Inline Chat</span><code>Cmd/Ctrl + K</code></div>
                  <div className="p-2 bg-bg-elevated rounded flex justify-between"><span>AI Panel</span><code>Cmd/Ctrl + Shift + A</code></div>
                  <div className="p-2 bg-bg-elevated rounded flex justify-between"><span>Accept Suggestion</span><code>Tab</code></div>
                  <div className="p-2 bg-bg-elevated rounded flex justify-between"><span>Next Suggestion</span><code>Alt + ]</code></div>
                </div>
              </div>

              <div className="card mb-6" id="custom-keybindings">
                <h3 className="heading-sm mb-4 flex items-center gap-2">
                  <Edit3 size={20} />
                  Custom Keybindings
                </h3>
                <p className="text-text-secondary mb-4">
                  Customize keyboard shortcuts to match your workflow.
                </p>

                <h4 className="font-semibold mb-2">Edit Keybindings</h4>
                <ol className="list-decimal list-inside text-text-secondary space-y-2 mb-4">
                  <li>Open Command Palette (<code className="px-1 py-0.5 bg-bg-elevated rounded text-xs">Cmd/Ctrl + Shift + P</code>)</li>
                  <li>Type "Open Keyboard Shortcuts"</li>
                  <li>Search for the command to rebind</li>
                  <li>Click the pencil icon and enter new shortcut</li>
                </ol>

                <h4 className="font-semibold mb-2">keybindings.json</h4>
                <div className="p-4 rounded-lg bg-bg-elevated font-mono text-sm">
                  <pre className="text-text-secondary">{`[
  {
    "key": "cmd+shift+d",
    "command": "editor.action.duplicateLine"
  },
  {
    "key": "cmd+k cmd+s",
    "command": "workbench.action.files.saveAll"
  }
]`}</pre>
                </div>
              </div>

              <div className="card" id="vim-mode">
                <h3 className="heading-sm mb-4 flex items-center gap-2">
                  <Command size={20} />
                  Vim Mode
                </h3>
                <p className="text-text-secondary mb-4">
                  Full Vim emulation for modal editing enthusiasts.
                </p>

                <div className="p-4 rounded-lg bg-accent-glow border border-accent/20 mb-4">
                  <h4 className="font-semibold mb-2">Enable Vim Mode</h4>
                  <p className="text-text-secondary text-sm">
                    Settings → Editor → Enable Vim Mode, or install the Vim extension.
                  </p>
                </div>

                <h4 className="font-semibold mb-2">Supported Features</h4>
                <ul className="list-disc list-inside text-text-secondary space-y-1 mb-4">
                  <li>All standard Vim motions (h, j, k, l, w, b, e, etc.)</li>
                  <li>Visual mode and visual block mode</li>
                  <li>Macros and registers</li>
                  <li>Ex commands (:w, :q, :s, etc.)</li>
                  <li>Custom .vimrc settings</li>
                </ul>

                <h4 className="font-semibold mb-2">Quick Reference</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 bg-bg-elevated rounded"><code>i</code> - Insert mode</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>v</code> - Visual mode</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>dd</code> - Delete line</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>yy</code> - Yank line</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>p</code> - Paste</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>u</code> - Undo</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>/</code> - Search</div>
                  <div className="p-2 bg-bg-elevated rounded"><code>:w</code> - Save</div>
                </div>
              </div>
            </section>

            {/* ==================== SETTINGS ==================== */}
            <section id="user-settings" className="mb-12">
              <h2 className="heading-lg mb-6 flex items-center gap-3">
                <Settings className="text-accent" />
                Settings
              </h2>

              <div className="card mb-6">
                <h3 className="heading-sm mb-4">User Settings</h3>
                <p className="text-text-secondary mb-4">
                  Global settings that apply to all projects.
                </p>

                <h4 className="font-semibold mb-2">Open Settings</h4>
                <ul className="list-disc list-inside text-text-secondary space-y-1 mb-4">
                  <li>Press <code className="px-1 py-0.5 bg-bg-elevated rounded">Cmd/Ctrl + ,</code></li>
                  <li>Or File → Preferences → Settings</li>
                </ul>

                <h4 className="font-semibold mb-2">Common Settings</h4>
                <div className="space-y-2 text-sm">
                  <div className="p-3 bg-bg-elevated rounded">
                    <div className="font-medium">editor.fontSize</div>
                    <div className="text-text-tertiary">Font size in pixels (default: 14)</div>
                  </div>
                  <div className="p-3 bg-bg-elevated rounded">
                    <div className="font-medium">editor.tabSize</div>
                    <div className="text-text-tertiary">Number of spaces for tab (default: 2)</div>
                  </div>
                  <div className="p-3 bg-bg-elevated rounded">
                    <div className="font-medium">editor.wordWrap</div>
                    <div className="text-text-tertiary">Enable word wrapping (default: off)</div>
                  </div>
                  <div className="p-3 bg-bg-elevated rounded">
                    <div className="font-medium">editor.formatOnSave</div>
                    <div className="text-text-tertiary">Format file on save (default: false)</div>
                  </div>
                </div>
              </div>

              <div className="card mb-6" id="workspace-settings">
                <h3 className="heading-sm mb-4 flex items-center gap-2">
                  <Folder size={20} />
                  Workspace Settings
                </h3>
                <p className="text-text-secondary mb-4">
                  Settings specific to the current project. Stored in .ochtopus/settings.json.
                </p>

                <h4 className="font-semibold mb-2">When to Use</h4>
                <ul className="list-disc list-inside text-text-secondary space-y-1 mb-4">
                  <li>Project-specific formatting rules</li>
                  <li>Team-shared configurations</li>
                  <li>Language-specific settings for project</li>
                  <li>Extension recommendations</li>
                </ul>

                <h4 className="font-semibold mb-2">Priority Order</h4>
                <div className="flex items-center gap-2 text-sm">
                  <span className="px-3 py-1 bg-bg-elevated rounded">Default</span>
                  <ChevronRight size={16} className="text-text-tertiary" />
                  <span className="px-3 py-1 bg-bg-elevated rounded">User</span>
                  <ChevronRight size={16} className="text-text-tertiary" />
                  <span className="px-3 py-1 bg-accent text-white rounded">Workspace</span>
                </div>
              </div>

              <div className="card mb-6" id="themes">
                <h3 className="heading-sm mb-4 flex items-center gap-2">
                  <Palette size={20} />
                  Themes
                </h3>
                <p className="text-text-secondary mb-4">
                  Customize the look and feel of your editor.
                </p>

                <h4 className="font-semibold mb-2">Built-in Themes</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm mb-4">
                  <div className="p-3 bg-bg-elevated rounded text-center">Ochtopus Dark</div>
                  <div className="p-3 bg-bg-elevated rounded text-center">Ochtopus Light</div>
                  <div className="p-3 bg-bg-elevated rounded text-center">Monokai</div>
                  <div className="p-3 bg-bg-elevated rounded text-center">GitHub</div>
                </div>

                <h4 className="font-semibold mb-2">Change Theme</h4>
                <ol className="list-decimal list-inside text-text-secondary space-y-1 mb-4">
                  <li>Press <code className="px-1 py-0.5 bg-bg-elevated rounded text-xs">Cmd/Ctrl + K, Cmd/Ctrl + T</code></li>
                  <li>Or Command Palette → "Preferences: Color Theme"</li>
                  <li>Use arrow keys to preview themes</li>
                  <li>Press Enter to select</li>
                </ol>

                <h4 className="font-semibold mb-2">Customize Colors</h4>
                <div className="p-4 rounded-lg bg-bg-elevated font-mono text-sm">
                  <pre className="text-text-secondary">{`// settings.json
{
  "workbench.colorCustomizations": {
    "editor.background": "#1a1a2e",
    "activityBar.background": "#16213e"
  }
}`}</pre>
                </div>
              </div>

              <div className="card" id="sync">
                <h3 className="heading-sm mb-4 flex items-center gap-2">
                  <Cloud size={20} />
                  Sync Settings
                </h3>
                <p className="text-text-secondary mb-4">
                  Keep your settings synchronized across all your devices.
                </p>

                <div className="p-4 rounded-lg bg-accent-glow border border-accent/20 mb-4">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <RefreshCw className="text-accent" size={18} />
                    Settings Sync
                  </h4>
                  <p className="text-text-secondary text-sm">
                    Sign in with your Ochtopus account to enable automatic settings sync.
                  </p>
                </div>

                <h4 className="font-semibold mb-2">What Gets Synced</h4>
                <ul className="list-disc list-inside text-text-secondary space-y-1 mb-4">
                  <li>User settings</li>
                  <li>Keyboard shortcuts</li>
                  <li>User snippets</li>
                  <li>Extensions</li>
                  <li>UI state (layout, open editors)</li>
                </ul>

                <h4 className="font-semibold mb-2">Enable Sync</h4>
                <ol className="list-decimal list-inside text-text-secondary space-y-1">
                  <li>Click the account icon in the Activity Bar</li>
                  <li>Sign in with your Ochtopus account</li>
                  <li>Select "Turn on Settings Sync"</li>
                  <li>Choose what to sync</li>
                </ol>
              </div>
            </section>

            {/* Security Section */}
            <section id="security" className="mb-12">
              <h2 className="heading-lg mb-6 flex items-center gap-3">
                <Shield className="text-accent" />
                Security & Privacy
              </h2>

              <div className="card">
                <h3 className="heading-sm mb-4">Your Code is Safe</h3>
                <p className="text-text-secondary mb-4">
                  We take security seriously. Here's how we protect your code:
                </p>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-bg-elevated">
                    <h4 className="font-semibold mb-2">SOC 2 Type II</h4>
                    <p className="text-text-secondary text-sm">Independently audited security controls.</p>
                  </div>
                  <div className="p-4 rounded-lg bg-bg-elevated">
                    <h4 className="font-semibold mb-2">End-to-End Encryption</h4>
                    <p className="text-text-secondary text-sm">AES-256 encryption for all data.</p>
                  </div>
                  <div className="p-4 rounded-lg bg-bg-elevated">
                    <h4 className="font-semibold mb-2">Zero Retention Mode</h4>
                    <p className="text-text-secondary text-sm">Enterprise option to never store code.</p>
                  </div>
                  <div className="p-4 rounded-lg bg-bg-elevated">
                    <h4 className="font-semibold mb-2">GDPR Compliant</h4>
                    <p className="text-text-secondary text-sm">Full compliance with privacy regulations.</p>
                  </div>
                </div>
              </div>
            </section>
          </main>

          {/* Quick Links Sidebar */}
          <aside className="hidden xl:block">
            <div className="sticky top-24">
              <h3 className="font-semibold text-sm mb-4">{t('docs.quickLinks.title')}</h3>
              <ul className="space-y-2">
                {quickLinks.map((link) => (
                  <li key={link.title}>
                    <a href={link.href} className="flex items-center gap-2 text-sm text-text-secondary hover:text-accent transition-colors">
                      <link.icon size={14} />
                      {link.title}
                    </a>
                  </li>
                ))}
              </ul>

              <div className="mt-8 p-4 rounded-lg bg-accent-glow border border-accent/20">
                <h4 className="font-semibold mb-2">{t('docs.needHelp.title')}</h4>
                <p className="text-text-secondary text-sm mb-3">{t('docs.needHelp.description')}</p>
                <a href="#" className="btn btn-primary btn-sm w-full">{t('docs.needHelp.contact')}</a>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
